import { z } from 'zod';
import { toCoreUserId, toDiffId, toModelId, toSnapshotId } from '@/shared/types/common.types';
import {
  dotNetDateSchema,
  dotNetDateToIso,
  guidSchema,
  numericEnum,
  parseByteSize,
} from '@/shared/utils/api/serverPrimitives';
import type {
  ChangeKind,
  DiffSummary,
  DiffTimelineEntry,
  ElementChange,
  ElementLocation,
  ModelDiff,
  ParameterChange,
  SnapshotMetadata,
  SnapshotStatistics,
} from '../types/snapshot.types';

/**
 * The wire contract for `SnapshotMetaDataController` and `RevitModelDiffsController`.
 *
 * Both serve EF entities directly rather than DTOs, so the field names are the domain
 * property names and every reference-typed field can be null at runtime regardless of what
 * the C# declaration says — the entities predate nullable reference types and their
 * repositories return rows that were written by earlier versions of the tracker.
 *
 * Collections are only populated when the endpoint eager-loads them, which is why a diff's
 * element collections default to empty rather than being required: `ByModel` and
 * `TimelineByModel` return diffs without them, and only `{id}` includes them.
 */

/**
 * `RevitElementType`.
 *
 * Unknown values fall back to `Element` rather than failing the response: the enum gains
 * members as the tracker learns to record more kinds, and a deployment where the server is
 * ahead of the client should still be able to show what changed.
 */
const elementKind = numericEnum('RevitElementType', {
  0: 'Element',
  1: 'Family',
  2: 'FamilyInstance',
  3: 'View',
  4: 'Category',
  5: 'Schedule',
  6: 'Sheet',
  7: 'FamilyType',
  8: 'BuiltInElementType',
  9: 'Workset',
  10: 'TrackedDesignElement',
} as const);

/** `RevitParameterChange`. */
const parameterChangeSchema = z.object({
  id: z.number().int().optional(),
  parameterName: z.string().nullish(),
  previousValue: z.string().nullish(),
  currentValue: z.string().nullish(),
});

/**
 * `RevitElementChange`.
 *
 * `ChangeType` is deliberately not read. The collection an element arrives in is what the
 * server filed it under, and the column defaults to `None` on rows the tracker wrote
 * without setting it — decoding it could only disagree with the collection or fail the
 * whole response over a field the UI does not use.
 */
const elementChangeSchema = z.object({
  id: z.number().int(),
  revitElementType: elementKind.schema.catch('Element'),
  // `long` on the server; may arrive as a string if it ever exceeds the safe range.
  elementId: z.union([z.number(), z.string()]),
  uniqueId: z.string().nullish(),
  elementFullName: z.string().nullish(),
  familyName: z.string().nullish(),
  familyTypeName: z.string().nullish(),
  changeDate: dotNetDateSchema.nullish(),
  changedByAuthorFullName: z.string().nullish(),
  isParametersChanged: z.boolean().default(false),
  isLocationChanged: z.boolean().default(false),
  previousLocationX: z.number().nullish(),
  previousLocationY: z.number().nullish(),
  previousLocationZ: z.number().nullish(),
  currentLocationX: z.number().nullish(),
  currentLocationY: z.number().nullish(),
  currentLocationZ: z.number().nullish(),
  parameterChanges: z.array(parameterChangeSchema).nullish(),
});

/** `SnapshotMetaData`. */
export const snapshotMetadataSchema = z.object({
  id: z.number().int(),
  name: z.string().nullish(),
  hashId: guidSchema.nullish(),
  revitModelFullName: z.string().nullish(),
  revitModelId: z.number().int(),
  // A `string` on the server, holding a byte count.
  snapshotFileSize: z.string().nullish(),
  snapshotIdentityName: z.string().nullish(),
  elementVersion: z.array(z.number().int()).nullish(),
  lastModified: dotNetDateSchema.nullish(),
  created: dotNetDateSchema.nullish(),
  authorFullName: z.string().nullish(),
  authorId: z.number().int().default(0),
  path: z.string().nullish(),
  title: z.string().nullish(),
  revitFamilyInstanceIds: z.array(z.number()).nullish(),
  revitProjectFamilyIds: z.array(z.number()).nullish(),
  hashSum: guidSchema.nullish(),
  modelVersion: z.number().int().default(0),
  lastSynchronized: dotNetDateSchema.nullish(),
  currentUniqueIds: z.array(z.string()).nullish(),
});

/** The anonymous object returned by `GetStatistics/{id}`. */
export const snapshotStatisticsSchema = z.object({
  id: z.number().int(),
  name: z.string().nullish(),
  familyInstancesCount: z.number().int(),
  projectFamiliesCount: z.number().int(),
  uniqueElementsCount: z.number().int(),
  totalElementsCount: z.number().int(),
  modelVersion: z.number().int(),
  fileSize: z.string().nullish(),
  lastSynchronized: dotNetDateSchema.nullish(),
  created: dotNetDateSchema.nullish(),
});

/** `RevitModelDiff`. */
export const modelDiffSchema = z.object({
  id: z.number().int(),
  name: z.string().nullish(),
  revitModelCurrentId: z.number().int(),
  revitModelCurrentVersion: z.array(z.number().int()).nullish(),
  revitModelCurrentName: z.string().nullish(),
  revitModelSnapshotNewId: z.number().int(),
  addedElements: z.array(elementChangeSchema).nullish(),
  removedElements: z.array(elementChangeSchema).nullish(),
  modifiedElements: z.array(elementChangeSchema).nullish(),
});

/** One item from `TimelineByModel/{modelId}`. */
export const diffTimelineEntrySchema = z.object({
  diffId: z.number().int(),
  currentModelId: z.number().int(),
  currentModelName: z.string().nullish(),
  currentModelVersion: z.array(z.number().int()).nullish(),
  snapshotNewId: z.number().int(),
  addedCount: z.number().int(),
  modifiedCount: z.number().int(),
  removedCount: z.number().int(),
  totalChanges: z.number().int(),
  date: dotNetDateSchema.nullish(),
  authorFullName: z.string().nullish(),
});

/** The `{ count }` object several snapshot endpoints answer with. */
export const countSchema = z.object({ count: z.number().int() });

/**
 * Resolves which model a diff belongs to.
 *
 * Both id columns hold a `RevitFIleDocument` id, and for a given model's history they hold
 * the same one — the entity's names are a leftover from an earlier design where the new
 * side was a snapshot. The exception is a first synchronisation, where the current side is
 * the `-1` sentinel because there was no earlier version; the new side is the model then.
 */
function resolveDiffModel(
  currentId: number,
  newId: number
): { readonly modelId: ReturnType<typeof toModelId>; readonly isInitialSync: boolean } {
  return currentId < 0
    ? { modelId: toModelId(newId), isInitialSync: true }
    : { modelId: toModelId(currentId), isInitialSync: false };
}

export function toSnapshotMetadata(raw: z.infer<typeof snapshotMetadataSchema>): SnapshotMetadata {
  const uniqueIds = raw.currentUniqueIds ?? [];

  return {
    id: toSnapshotId(raw.id),
    modelId: toModelId(raw.revitModelId),
    modelFullName: raw.revitModelFullName ?? raw.name ?? '',
    identityName: raw.snapshotIdentityName ?? '',
    title: raw.title ?? '',
    path: raw.path ?? '',
    modelVersion: raw.modelVersion,
    elementVersion: raw.elementVersion ?? [],
    createdAt: dotNetDateToIso(raw.created),
    lastModified: dotNetDateToIso(raw.lastModified),
    lastSynchronized: dotNetDateToIso(raw.lastSynchronized),
    authorId: toCoreUserId(raw.authorId),
    authorFullName: raw.authorFullName ?? '',
    fileSizeBytes: parseByteSize(raw.snapshotFileSize),
    hashSum: raw.hashSum ?? '',
    uniqueIds,
    elementCount: uniqueIds.length,
    familyInstanceCount: raw.revitFamilyInstanceIds?.length ?? 0,
    projectFamilyCount: raw.revitProjectFamilyIds?.length ?? 0,
  };
}

export function toSnapshotStatistics(
  raw: z.infer<typeof snapshotStatisticsSchema>
): SnapshotStatistics {
  return {
    id: toSnapshotId(raw.id),
    name: raw.name ?? '',
    familyInstanceCount: raw.familyInstancesCount,
    projectFamilyCount: raw.projectFamiliesCount,
    uniqueElementCount: raw.uniqueElementsCount,
    totalElementCount: raw.totalElementsCount,
    modelVersion: raw.modelVersion,
    fileSizeBytes: parseByteSize(raw.fileSize),
    lastSynchronized: dotNetDateToIso(raw.lastSynchronized),
    createdAt: dotNetDateToIso(raw.created),
  };
}

/**
 * Reads a location from the six flat coordinate columns the server stores.
 *
 * All three axes have to be present for a point to mean anything, so a partially recorded
 * location reads as no location rather than as the origin.
 */
function toLocation(
  x: number | null | undefined,
  y: number | null | undefined,
  z: number | null | undefined
): ElementLocation | null {
  if (x == null || y == null || z == null) return null;
  return { x, y, z };
}

/**
 * Derives a parameter change's kind from which side holds a value.
 *
 * The server does not label these: it writes `CurrentValue = null` for a parameter that was
 * deleted and `PreviousValue = null` for one that was added, and both sides present means
 * the value was edited.
 */
function toParameterChange(raw: z.infer<typeof parameterChangeSchema>): ParameterChange {
  const previousValue = raw.previousValue ?? null;
  const currentValue = raw.currentValue ?? null;

  return {
    parameterName: raw.parameterName ?? '',
    previousValue,
    currentValue,
    changeType: previousValue === null ? 'added' : currentValue === null ? 'removed' : 'modified',
  };
}

export function toElementChange(
  raw: z.infer<typeof elementChangeSchema>,
  filedUnder: ChangeKind
): ElementChange {
  return {
    id: raw.id,
    /*
     * The collection the element arrived in is authoritative over its own `ChangeType`.
     * They agree in practice, but the collection is what the server used to file it, and
     * `ChangeType` defaults to `None` on rows written without setting it — a row appearing
     * under "added" while badged "modified" would be indefensible.
     */
    changeKind: filedUnder,
    elementKind: raw.revitElementType,
    elementId: Number(raw.elementId),
    uniqueId: raw.uniqueId ?? '',
    name: raw.elementFullName ?? '',
    familyName: raw.familyName ?? '',
    familyTypeName: raw.familyTypeName ?? '',
    changedAt: dotNetDateToIso(raw.changeDate),
    changedByFullName: raw.changedByAuthorFullName ?? '',
    isParametersChanged: raw.isParametersChanged,
    isLocationChanged: raw.isLocationChanged,
    previousLocation: toLocation(
      raw.previousLocationX,
      raw.previousLocationY,
      raw.previousLocationZ
    ),
    currentLocation: toLocation(raw.currentLocationX, raw.currentLocationY, raw.currentLocationZ),
    parameterChanges: (raw.parameterChanges ?? []).map(toParameterChange),
  };
}

export function summariseDiff(added: number, removed: number, modified: number): DiffSummary {
  const totalChangeCount = added + removed + modified;

  return {
    addedCount: added,
    removedCount: removed,
    modifiedCount: modified,
    totalChangeCount,
    hasChanges: totalChangeCount > 0,
  };
}

export function toModelDiff(raw: z.infer<typeof modelDiffSchema>): ModelDiff {
  const added = (raw.addedElements ?? []).map((element) => toElementChange(element, 'Added'));
  const removed = (raw.removedElements ?? []).map((element) => toElementChange(element, 'Removed'));
  const modified = (raw.modifiedElements ?? []).map((element) =>
    toElementChange(element, 'Modified')
  );

  return {
    id: toDiffId(raw.id),
    ...resolveDiffModel(raw.revitModelCurrentId, raw.revitModelSnapshotNewId),
    modelName: raw.revitModelCurrentName ?? raw.name ?? '',
    modelVersion: raw.revitModelCurrentVersion ?? [],
    added,
    removed,
    modified,
    summary: summariseDiff(added.length, removed.length, modified.length),
  };
}

export function toTimelineEntry(raw: z.infer<typeof diffTimelineEntrySchema>): DiffTimelineEntry {
  return {
    id: toDiffId(raw.diffId),
    ...resolveDiffModel(raw.currentModelId, raw.snapshotNewId),
    modelName: raw.currentModelName ?? '',
    modelVersion: raw.currentModelVersion ?? [],
    addedCount: raw.addedCount,
    removedCount: raw.removedCount,
    modifiedCount: raw.modifiedCount,
    totalChangeCount: raw.totalChanges,
    changedAt: dotNetDateToIso(raw.date),
    authorFullName: raw.authorFullName ?? '',
  };
}
